import React, { memo } from 'react';

import { getArcPercent, getTieredStatusVisual } from '../../utils/metricAttainment';
import { TieredMetricColors } from '../metricMonthlySummaryCard/GradientDefs';

interface TieredMetricGaugeProps {
    valueText: string;
    colorIndex: number;
    metricOmitted?: boolean;
    attainmentRank?: number | null;
    attainmentLevelLabel?: string | null;
    metricValue?: number | null;
    min?: number;
    max?: number;
    meetsTarget?: number | null;
    exceedsTarget?: number | null;
}

// Isolated on purpose: this file owns every constant its own gauge needs (size, stroke widths,
// gap). Nothing here is named the same as anything in LegacyMetricGauge.tsx, so a future change
// to one can never silently change the other's rendering - that's exactly what happened when
// both gauges shared a single STROKE_WIDTH constant in one file.
const GAUGE_SIZE = 200;
const TIERED_STROKE_WIDTH = 30;
const SEAM_GAP_DEGREES = 0.5;
const VALUE_ARC_STROKE_WIDTH = 8;
const BAND_CORNER_RADIUS = 8;
const VALUE_CORNER_RADIUS = 3;

const polarPoint = (cx: number, cy: number, r: number, angleDeg: number) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
        x: cx + r * Math.cos(angleRad),
        y: cy - r * Math.sin(angleRad),
    };
};

// Point at `distance` from `from`, moving toward `to` along the straight line between them.
const pointTowards = (
    from: { x: number; y: number },
    to: { x: number; y: number },
    distance: number
) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return { x: from.x + (dx / len) * distance, y: from.y + (dy / len) * distance };
};

// Converts a 0-100 arc percent into the "end angle" for the dome, sweeping from 180 (0%) down to
// 0 (100%).
const percentToEndAngle = (percent: number) => 180 * (1 - percent / 100);

/**
 * Draws one "band" of the dome as a FILLED shape - flat straight edge at each end (the visible
 * cap face), with small rounded fillets only at the 4 corners where that flat face meets the two
 * long curving sides. This is the "stadium" cross-section from the design reference: SVG's
 * native stroke-linecap only offers butt (flat, sharp corners)/round (full semicircle cap)/square
 * - none of which produce a flat cap with independently-sized corner rounding - so this has to be
 * built as an explicit closed path (two arcs + two flat edges + four quadratic-bezier fillets)
 * rather than a stroked line.
 */
const describeFlatEdgeRoundedCornerBand = (
    cx: number,
    cy: number,
    r: number,
    fromDeg: number,
    toDeg: number,
    thickness: number,
    cornerRadius: number
) => {
    const outerR = r + thickness / 2;
    const innerR = r - thickness / 2;

    // Shrink the corner radius rather than let the geometry invert/overlap when the arc's span
    // or thickness is too small to fit the requested rounding.
    const spanRad = ((fromDeg - toDeg) / 2) * (Math.PI / 180);
    const maxRadiusForSpan = spanRad * Math.min(outerR, innerR) * 0.9;
    const safeCornerRadius = Math.max(0, Math.min(cornerRadius, thickness / 2, maxRadiusForSpan));

    const insetAngleOuter = (safeCornerRadius / outerR) * (180 / Math.PI);
    const insetAngleInner = (safeCornerRadius / innerR) * (180 / Math.PI);

    const outerStartCorner = polarPoint(cx, cy, outerR, fromDeg);
    const outerEndCorner = polarPoint(cx, cy, outerR, toDeg);
    const innerEndCorner = polarPoint(cx, cy, innerR, toDeg);
    const innerStartCorner = polarPoint(cx, cy, innerR, fromDeg);

    const outerArcStart = polarPoint(cx, cy, outerR, fromDeg - insetAngleOuter);
    const outerArcEnd = polarPoint(cx, cy, outerR, toDeg + insetAngleOuter);
    const innerArcEnd = polarPoint(cx, cy, innerR, toDeg + insetAngleInner);
    const innerArcStart = polarPoint(cx, cy, innerR, fromDeg - insetAngleInner);

    const chordNearOuterStart = pointTowards(outerStartCorner, innerStartCorner, safeCornerRadius);
    const chordNearInnerStart = pointTowards(innerStartCorner, outerStartCorner, safeCornerRadius);
    const chordNearOuterEnd = pointTowards(outerEndCorner, innerEndCorner, safeCornerRadius);
    const chordNearInnerEnd = pointTowards(innerEndCorner, outerEndCorner, safeCornerRadius);

    return [
        `M ${outerArcStart.x} ${outerArcStart.y}`,
        `A ${outerR} ${outerR} 0 0 1 ${outerArcEnd.x} ${outerArcEnd.y}`,
        `Q ${outerEndCorner.x} ${outerEndCorner.y} ${chordNearOuterEnd.x} ${chordNearOuterEnd.y}`,
        `L ${chordNearInnerEnd.x} ${chordNearInnerEnd.y}`,
        `Q ${innerEndCorner.x} ${innerEndCorner.y} ${innerArcEnd.x} ${innerArcEnd.y}`,
        `A ${innerR} ${innerR} 0 0 0 ${innerArcStart.x} ${innerArcStart.y}`,
        `Q ${innerStartCorner.x} ${innerStartCorner.y} ${chordNearInnerStart.x} ${chordNearInnerStart.y}`,
        `L ${chordNearOuterStart.x} ${chordNearOuterStart.y}`,
        `Q ${outerStartCorner.x} ${outerStartCorner.y} ${outerArcStart.x} ${outerArcStart.y}`,
        'Z',
    ].join(' ');
};

/**
 * The P4P multi-tier gauge (E20-6317). Renders 4 stacked concentric bands, each a straightforward
 * 0-100% position computed via getArcPercent (value/meets/exceeds all normalized against the
 * same min/max, which the backend already orients "worst->best" per metric direction):
 *   1. reference - always 0-100%, bottom layer, bright/track color
 *   2. exceeds   - 0-exceedsPercent%, drawn over reference
 *   3. meets     - 0-meetsPercent%, drawn over exceeds (dark, "wins" visually up to meets)
 *   4. value     - 0-valuePercent%, thin white band at the SAME centerline radius as the main
 *                  bands (centered within their thickness), drawn last - the actual live value
 * Each layer only shows the portion the one on top of it doesn't cover, which is why Exceeds
 * only shows as a sliver past Meets, and Reference only shows as a sliver past Exceeds.
 */
export const TieredMetricGauge = memo(({
    valueText,
    colorIndex,
    metricOmitted,
    attainmentRank,
    attainmentLevelLabel,
    metricValue,
    min,
    max,
    meetsTarget,
    exceedsTarget,
}: TieredMetricGaugeProps) => {
    const gradientIndex = metricOmitted ? 5 : colorIndex;

    const statusTextColor = metricOmitted
        ? '#3F5765'
        : getTieredStatusVisual(attainmentLevelLabel, attainmentRank).color;

    const colorTriple = TieredMetricColors[gradientIndex] ?? TieredMetricColors[0];
    const safeMin = min ?? 0;
    const safeMax = max ?? 100;

    const exceedsPercent = getArcPercent(exceedsTarget, safeMin, safeMax);
    const meetsPercent = getArcPercent(meetsTarget, safeMin, safeMax);
    const valuePercent = getArcPercent(metricValue, safeMin, safeMax);

    const cx = GAUGE_SIZE / 2;
    const cy = GAUGE_SIZE / 2 + 16;
    const r = GAUGE_SIZE / 2 - TIERED_STROKE_WIDTH / 2 - 4;

    return (
        <svg width={GAUGE_SIZE} height={GAUGE_SIZE} viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`}>
            {/* 1. Reference - always full 0-100% */}
            <path
                d={describeFlatEdgeRoundedCornerBand(cx, cy, r, 180, 0, TIERED_STROKE_WIDTH, BAND_CORNER_RADIUS)}
                fill={colorTriple.reference}
                stroke="none"
            />
            {/* 2. Exceeds - 0 to exceedsPercent% */}
            {exceedsPercent > 0 && (
                <path
                    d={describeFlatEdgeRoundedCornerBand(
                        cx,
                        cy,
                        r,
                        180,
                        percentToEndAngle(exceedsPercent) - SEAM_GAP_DEGREES,
                        TIERED_STROKE_WIDTH,
                        BAND_CORNER_RADIUS
                    )}
                    fill={colorTriple.light}
                    stroke="none"
                />
            )}
            {/* 3. Meets - 0 to meetsPercent%, on top */}
            {meetsPercent > 0 && (
                <path
                    d={describeFlatEdgeRoundedCornerBand(
                        cx,
                        cy,
                        r,
                        180,
                        percentToEndAngle(meetsPercent) - SEAM_GAP_DEGREES,
                        TIERED_STROKE_WIDTH,
                        BAND_CORNER_RADIUS
                    )}
                    fill={colorTriple.dark}
                    stroke="none"
                />
            )}
            {/* 4. Value - thin white band at the live value's position, drawn at the SAME
                centerline radius as the main bands so it sits exactly centered within their
                thickness. */}
            {valuePercent > 0 && (
                <path
                    d={describeFlatEdgeRoundedCornerBand(
                        cx,
                        cy,
                        r,
                        180,
                        percentToEndAngle(valuePercent),
                        VALUE_ARC_STROKE_WIDTH,
                        VALUE_CORNER_RADIUS
                    )}
                    fill="#FFFFFF"
                    fillOpacity={0.9}
                    stroke="none"
                />
            )}
            <text
                x={cx}
                y={cy - 10}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="34"
                fontWeight="800"
                fill={statusTextColor}
            >
                {valueText}
            </text>
        </svg>
    );
});
TieredMetricGauge.displayName = 'TieredMetricGauge';

export default TieredMetricGauge;
